import * as Sentry from '@sentry/browser';
import fetch from 'isomorphic-fetch';
import Cookies from 'js-cookie';
import debounce from 'lodash/debounce';
import moment from 'moment';
import { createAction } from 'redux-actions';
import { subscriptionActions } from 'src/app/subscription/subscription-actions';
import { ONE_GB_IN_BYTES } from 'src/common/constants';
import { sendLogToServer } from 'src/common/logging/logging-api';
import { getUser } from 'src/common/state/me/me-api';
import { MEDIA_STATUS } from 'src/common/types/media';
import { selectActiveLabelId } from 'src/dashboard/state/labels/labels-selectors';
import { dashboardUpsellActions } from 'src/dashboard/state/upsell/upsell-actions';
import { videosActions } from 'src/dashboard/state/videos/videos-actions';
import { transformVideoAfterFetch } from 'src/dashboard/utils/transform-video-after-fetch';
import { selectStorageUpsellTargetPlanId } from 'src/pricing/state/pricing-selectors';
import { disableDarkMode } from 'src/utils/disable-dark-mode';
import { enableDarkMode } from 'src/utils/enable-dark-mode';
import { generateUniqueId } from 'src/utils/generate-unique-id';
import { isDarkModeEnabled } from 'src/utils/is-dark-mode-enabled';
import { setDarkModeCookie } from 'src/utils/set-dark-mode-cookie';
import URLSearchParams from 'url-search-params';

import { upload, transcode } from '../api/video';
import { createFetchVideoRequest } from '../api/videos';
import {
  getUploads,
  getVideo,
  getSelectedVideos,
  selectIsMobile,
  getAllVideos,
} from '../misc/selectors';
import { getBestSourceUrl } from '../misc/utilities';
import { trackUploadCompleted, trackUploadProgress } from './uploads';

export const POLLING_UPDATE_RECEIVED = 'POLLING_UPDATE_RECEIVED';
export const FETCH_VIDEO = 'FETCH_VIDEO';

export const CLEAR_VIDEOS = 'CLEAR_VIDEOS';
export const clearVideosAction = createAction(CLEAR_VIDEOS);
export const CLEAR_VIDEO = 'CLEAR_VIDEO';

export const UPLOAD = 'UPLOAD';
export const uploadAction = createAction(UPLOAD);
export const UPLOAD_PROGRESS = 'UPLOAD_PROGRESS';
export const uploadProgressAction = createAction(UPLOAD_PROGRESS);
export const RETRANSCODE = 'RETRANSCODE';

export const SHORTCODE_RECEIVED = 'SHORTCODE_RECEIVED';

export const SORT_CHANGED = 'SORT_CHANGED';
export const SEARCH_CHANGED = 'SEARCH_CHANGED';
export const RESET_FILTERS = 'RESET_FILTERS';

export const LOGIN = 'LOGIN';

export const FETCH_CURRENT_USER = 'FETCH_CURRENT_USER';
export const fetchCurrentUserAction = createAction(FETCH_CURRENT_USER);

export const EDIT_USER = 'EDIT_USER';
export const editUserAction = createAction(EDIT_USER);
export const EDIT_VIDEO = 'EDIT_VIDEO';
export const editVideoAction = createAction(EDIT_VIDEO);

export const FETCH_STATS = 'FETCH_STATS';

export const DESELECT = 'DESELECT';
export const SELECT = 'SELECT';
export const DESELECT_ALL = 'DESELECT_ALL';
export const deselectAllAction = createAction(DESELECT_ALL);

export const EXTRACT_URL = 'EXTRACT_URL';
export const CLEAR_CLIPPER = 'CLEAR_CLIPPER';
export const SET_THUMBNAIL_MODE = 'SET_THUMBNAIL_MODE';

export const ZOOM = 'ZOOM';

export const SET_STATS_DOMAIN = 'SET_STATS_DOMAIN';
export const SET_STATS_INTERVAL = 'SET_STATS_INTERVAL';

export const MESSAGE = 'MESSAGE';

export const SWITCH_VIDEOS_VIEW = 'SWITCH_VIDEOS_VIEW';

export const REMOVE_SUB_ACCOUNT = 'REMOVE_SUB_ACCOUNT';

export const ARTICLE_EXCLUDED = 'ARTICLE_EXCLUDED';

export const UPDATE_EMBED = 'UPDATE_EMBED';

export const SUBSCRIBE = 'SUBSCRIBE';
export const UPDATE_CARD = 'UPDATE_CARD';

export const NOTIFY = 'NOTIFY';

export const CREATE_TEMP_VIDEO = 'CREATE_TEMP_VIDEO';

function onUserIsFetched(user, options = { activateDarkMode: false }) {
  setDarkModeCookie(user);

  if (options.activateDarkMode) {
    isDarkModeEnabled() ? enableDarkMode() : disableDarkMode();
  }

  setTimeout(() => {
    var data = {
      version: process.env.REACT_APP_VERSION,
    };
    if (user.user_name) {
      data.name = user.user_name + ' [' + user.plan_name + ']';
      if (user.user_name.includes('@')) {
        data.email = user.user_name;
      }
      data.payment_processor = user.payment_processor;
      data.plan_name = user.plan;
      data.subscription_status = user.subscription_status;
      data.user_since = moment.unix(user.date_added).fromNow();
    }

    window.Beacon('identify', data);
  }, 0);
}

// NOTE: this is deprecated and should not be used anymore.
// Instead you should be using the `logMessage` action that is hooked up with
// the logging-saga.
export function log(message) {
  return (_, getState) => {
    const { me } = getState();
    return sendLogToServer(message, me.version);
  };
}

const NEW_API_ACTION = ['billing_info', 'flags', 'usage'];

export function fetchExtraUserInfo(type) {
  return (dispatch) => {
    const newAPI = NEW_API_ACTION.includes(type);
    let url;

    if (newAPI) {
      if (type === 'billing_info') {
        url = `${process.env.REACT_APP_NEW_API_HOST}/payments/billing_info`;
      } else {
        url = `${process.env.REACT_APP_NEW_API_HOST}/me/${type}`;
      }
    } else {
      url = `${process.env.REACT_APP_API_HOST}/me/${type}`;
    }

    return fetch(url, {
      credentials: 'include',
    })
      .then(async function (response) {
        if (!response.ok) {
          throw Error(await response.text());
        }
        return response.json();
      })
      .then((json) => {
        dispatch(createAction(FETCH_CURRENT_USER)(json));

        return json;
      })
      .catch((e) => {});
  };
}

export function fetchCurrentUser() {
  return (dispatch) => {
    dispatch(fetchCurrentUserAction({ isFetching: true }));
    return getUser()
      .then((user) => {
        dispatch(fetchCurrentUserAction({ isFetching: false, ...user }));
        if (user.user_name) {
          onUserIsFetched(user);
        }

        dispatch(fetchExtraUserInfo('flags'));

        if (WebSocket) {
          const ws = new WebSocket(user.socket);
          ws.onmessage = (e) => {
            const parsedMessage = JSON.parse(e.data);
            if (parsedMessage.type === 'ping') {
              ws.send(JSON.stringify({ type: 'pong' }));
              return;
            }
            dispatch(createAction(MESSAGE)(parsedMessage));
          };
          dispatch(editUserAction({ ws }));
        }
        return user;
      })
      .catch((error) => {
        Sentry.captureException(error);
        dispatch(fetchCurrentUserAction(error));
      });
  };
}

export function userSubscribePlan({
  paymentMethodId,
  plan,
  subscriptionId,
  token,
  noTrial,
  defaultPlan,
  blackFriday = false,
}) {
  return (dispatch, getState) => {
    let url = `${process.env.REACT_APP_NEW_API_HOST}/me/subscribe/${plan}`;
    dispatch(createAction(SUBSCRIBE)({ purchasing: true }));

    const searchParams = new URLSearchParams(window.location.search);
    const srcInternal = searchParams.get('src_internal');

    const body = {
      token,
      src_internal: srcInternal,
    };
    if (noTrial) {
      body.notrial = true;
    }
    if (blackFriday) {
      body.blackFriday = true;
      body.notrial = true;
    }
    if (paymentMethodId) {
      body.payment_method = paymentMethodId;
    } else if (subscriptionId) {
      body.subscription_id = subscriptionId;
    } else {
      throw new Error('No payment method specified.');
    }

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify(body),
      credentials: 'include',
    })
      .then(async function (response) {
        if (!response.ok) {
          if (response.status == 429) {
            throw new Error('Too many attempts. Please try again later.');
          } else {
            throw new Error((await response.json()).message);
          }
        }
        dispatch(
          log(
            `CHECKOUT EXPERIMENT(subscription): ${JSON.stringify({
              trial: !body.notrial,
              defaultPlan,
              src_internal: srcInternal || null,
              selectedPlan: plan,
              userName: getState().me.user_name,
              subscribedAt: new Date(),
            })}`
          )
        );

        return response.json();
      })
      .then(async function (user) {
        await dispatch(createAction(FETCH_CURRENT_USER)(user));
        dispatch(createAction(SUBSCRIBE)({ purchasing: false }));
        dispatch(
          subscriptionActions.userSubscriptionToAPlan.fulfilled({
            plan,
            user,
          })
        );
        return user;
      })
      .catch((error) => {
        dispatch(
          log(`[FE Error] CHECKOUT STRIPE SUBSCRIPTION FAILED - ${error}`)
        );
        Sentry.captureException(error);
        dispatch(createAction(SUBSCRIBE)(error));
      });
  };
}

export function userChangePayment(paymentMethodId, token) {
  return (dispatch) => {
    let url = `${process.env.REACT_APP_NEW_API_HOST}/me/card`;
    dispatch(createAction(UPDATE_CARD)({ purchasing: true }));
    return fetch(url, {
      credentials: 'include',
      headers: {
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        payment_method: paymentMethodId,
        token,
      }),
    })
      .then(async function (response) {
        if (!response.ok) {
          if (response.status == 429) {
            throw Error('Too many attempts. Please try again later.');
          } else {
            throw Error((await response.json()).message);
          }
        }
        return response.json();
      })
      .then(async function (json) {
        await dispatch(createAction(FETCH_CURRENT_USER)(json));
        dispatch(createAction(UPDATE_CARD)({ purchasing: false }));
        return json;
      })
      .catch((error) => {
        Sentry.captureException(error);
        dispatch(createAction(UPDATE_CARD)(error));
      });
  };
}
export function fetchVideo(shortcode, version) {
  return (dispatch, getState) => {
    if (getState().video && getState().video.shortcode !== shortcode) {
      dispatch(createAction(CLEAR_VIDEO)());
    }
    dispatch(createAction(FETCH_VIDEO)({ isFetching: true, shortcode }));
    let url = `${process.env.REACT_APP_NEW_API_HOST}/videos/${shortcode}`;
    if (version) {
      url += `?version=${version}`;
    }

    return fetch(url, {
      credentials: 'include',
      headers: {
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
    })
      .then(async function (response) {
        if (!response.ok) {
          throw Error(await response.text());
        }
        return response.json();
      })
      .then((json) => {
        dispatch(createAction(FETCH_VIDEO)({ isFetching: false, ...json }));
        return json;
      })
      .catch((error) => {
        Sentry.captureException(error);
        dispatch(createAction(FETCH_VIDEO)(error));
      });
  };
}

function renameVideoInternal(shortcode, title) {
  if (typeof shortcode === 'undefined') {
    return;
  }

  return fetch(
    `${process.env.REACT_APP_NEW_API_HOST}/videos/${shortcode}/rename`,
    {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({ title }),
    }
  )
    .then(async (response) => {
      if (!response.ok) {
        const error = await response.json();
        throw Error(error.message);
      }
    })
    .catch((error) => {
      Sentry.captureException(error);
    });
}
function updatePrivacySettingsInternal(shortcode, privacySettings) {
  return fetch(
    `${process.env.REACT_APP_NEW_API_HOST}/videos/${shortcode}/privacy`,
    {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify(privacySettings),
    }
  )
    .then(async (response) => {
      if (!response.ok) {
        const error = await response.json();
        throw Error(error.message);
      }
    })
    .catch((error) => {
      Sentry.captureException(error);
    });
}
function initVideoInternal(
  shortcode,
  { original_size, original_name, upload_source, title }
) {
  return fetch(
    `${process.env.REACT_APP_NEW_API_HOST}/videos/${shortcode}/initialize`,
    {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({
        original_size,
        original_name,
        upload_source,
        title,
      }),
    }
  )
    .then(async (response) => {
      if (!response.ok) {
        const error = await response.json();
        throw Error(error.message);
      }
    })
    .catch((error) => {
      Sentry.captureException(error);
    });
}

export function errorVideo(shortcode, error) {
  return (dispatch) => {
    dispatch(editVideoAction({ shortcode, status: MEDIA_STATUS.ERROR, error }));

    return fetch(
      `${process.env.REACT_APP_NEW_API_HOST}/videos/${shortcode}/error`,
      {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Pragma: 'no-cache',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({ error }),
      }
    )
      .then(async (response) => {
        if (!response.ok) {
          const data = await response.json();
          throw Error(data.message);
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
      });
  };
}
export function renameVideo(shortcode, title, debounced) {
  return (dispatch) => {
    dispatch(editVideoAction({ isSaving: true, shortcode, title }));

    if (debounced) {
      renameVideoDebounced(shortcode, title);
    } else {
      return renameVideoInternal(shortcode, title);
    }
  };
}
const renameVideoDebounced = debounce(renameVideoInternal, 1000);

export function updatePrivacySettings(shortcode, privacySettings) {
  return (dispatch) => {
    dispatch(editVideoAction({ shortcode, ...privacySettings }));

    return updatePrivacySettingsInternal(shortcode, privacySettings);
  };
}

export function initVideo(shortcode, payload) {
  return (dispatch) => {
    dispatch(editVideoAction({ shortcode, ...payload }));

    initVideoInternal(shortcode, payload);
  };
}

export function cancelVideo(shortcode) {
  return fetch(
    `${process.env.REACT_APP_NEW_API_HOST}/videos/${shortcode}/cancel`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      credentials: 'include',
    }
  )
    .then(async (response) => {
      if (!response.ok) {
        const error = await response.json();
        throw Error(error.message);
      }
    })
    .catch((error) => {
      Sentry.captureException(error);
    });
}

export function cancelVideoEdits({ shortcode, version }) {
  return (dispatch, getState) =>
    fetch(
      `${process.env.REACT_APP_NEW_API_HOST}/videos/${shortcode}/cancelEdits`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Pragma: 'no-cache',
          'Cache-Control': 'no-cache',
        },
        credentials: 'include',
        body: JSON.stringify({ version }),
      }
    )
      .then((response) => response.json())
      .then((video) => {
        dispatch(editVideoAction(video));
        return video;
      });
}

export function revertVideo(shortcode) {
  return (dispatch) =>
    fetch(`${process.env.REACT_APP_NEW_API_HOST}/videos/${shortcode}/revert`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({ targetVersion: 0 }),
    })
      .then(async (response) => {
        if (!response.ok) {
          const error = await response.json();
          throw Error(error.message);
        }
        return response.json();
      })
      .then((response) => {
        dispatch(editVideoAction({ shortcode, ...response }));
      })
      .catch((error) => {
        Sentry.captureException(error);
      });
}
export function createVideoFromFile(file, options = {}) {
  return (dispatch, getState) => {
    const state = getState();
    const {
      me,
      subscription: { currentPlan },
    } = state;
    const activeLabelId = selectActiveLabelId(state);
    let fileName;
    if (!selectIsMobile(state)) {
      const n = file.name.lastIndexOf('.');
      fileName = n > -1 ? file.name.substr(0, n) : file.name;
    }

    // Show the storage upsell modal
    const upsellPlanId = selectStorageUpsellTargetPlanId(state);
    if (upsellPlanId) {
      dispatch(
        dashboardUpsellActions.storageUpsellModal.triggered(upsellPlanId)
      );
    }

    if (!currentPlan && me.plan_max_size) {
      if (file.size > me.plan_max_size * ONE_GB_IN_BYTES) {
        let maxSizeStr = `${me.plan_max_size.toString()}GB`;
        if (me.plan_max_size < 1) {
          maxSizeStr = `${(me.plan_max_size * 1000).toString()}MB`;
        }
        const message = `This video exceeds the max file size for your plan (${maxSizeStr})`;
        dispatch(editUserAction({ videoExceedsLimits: 'video-toolarge' }));
        dispatch(
          log(
            'File upload too large: ' +
              fileName +
              ' (' +
              file.type +
              ', ' +
              file.size +
              ' bytes)'
          )
        );
        throw new Error(message);
      }
    }

    const local_id = generateUniqueId('alpha', 8);
    // Watch out, these might be overriden later by the editVideo
    dispatch(
      videosActions.upload.createVideo({
        local_id,
        title: options.title || fileName,
        ...(activeLabelId ? { labels: [{ id: activeLabelId }] } : undefined),
      })
    );
    if (!file.size) {
      const error = 'Invalid file size (Try Safari if on iOS)';
      dispatch(
        editVideoAction({
          shortcode: local_id,
          status: MEDIA_STATUS.ERROR,
          error,
        })
      );
      throw new Error('Invalid file size (Try Safari if on iOS)');
    }

    const query = new URLSearchParams({
      size: file.size,
      version: me.version,
    });
    if (activeLabelId) {
      query.append('label_id', activeLabelId);
    }
    const url = `${process.env.REACT_APP_NEW_API_HOST}/uploads/shortcode?${query}`;
    return fetch(url, {
      credentials: 'include',
      headers: {
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          const error = await response.json();
          throw Error(error.message);
        }
        return response.json();
      })
      .then((metadata) => {
        if (!metadata.shortcode) {
          throw Error('No shortcode present in metadata');
        }
        const shortcode = metadata.shortcode;
        const upload_size = file.size;
        metadata.local_id = local_id;

        dispatch(createAction(SHORTCODE_RECEIVED)(metadata));
        dispatch(
          initVideo(shortcode, {
            original_size: upload_size,
            original_name: file.name,
            upload_source: options.upload_source,
            title: options.title || fileName,
          })
        );

        let recentSpeeds = [];
        const startTime = Date.now();
        const task = upload(
          file,
          { ...metadata.options, ...options },
          metadata,
          (err, data) => {
            if (err) {
              dispatch(
                log(
                  `[FE Error] upload task from createVideoFromFile failed for ${shortcode} with error: ${err.message}`
                )
              );
              dispatch(errorVideo(shortcode, err.message));
              return;
            }

            const upload_time = Date.now() - startTime;
            dispatch(
              uploadAction({
                shortcode,
                upload_metadata: metadata,
                transcode_options: options,
                transcode_data: data,
                upload_time,
                upload_percent: 100,
                percent: 0,
                waitingToTranscode: true,
              })
            );
            trackUploadCompleted(shortcode);
          },
          (data) => {
            if (data.speed != null) {
              recentSpeeds.unshift(data.speed > 0 ? data.speed : 0);
            }
            recentSpeeds = recentSpeeds.slice(0, 100);
            let sum = recentSpeeds.reduce(function (a, b) {
              return a + b;
            }, 0);
            let avg = sum / recentSpeeds.length;
            dispatch(
              uploadProgressAction({
                shortcode,
                upload_percent: data.percent,
                upload_speed: avg,
                retries: data.retries,
              })
            );
            trackUploadProgress(shortcode, Math.round(data.percent));
          },
          () => {
            dispatch(
              editVideoAction({
                shortcode,
                waitingToUpload: false,
              })
            );
          },
          (message) => {
            if (!message.includes('0 retries] uploadPart(')) {
              dispatch(log(`[FE Error] error ${message}`));
            }
          }
        );

        dispatch(
          editVideoAction({
            shortcode,
            waitingToUpload: task.queued,
            task,
          })
        );

        return metadata;
      })
      .catch((err) => {
        Sentry.captureException(err);
        dispatch(
          editVideoAction({
            shortcode: local_id,
            status: MEDIA_STATUS.ERROR,
            error: err.message,
          })
        );
      });
  };
}

export function createVideoFromFiles(files, options) {
  return (dispatch) => {
    const promises = [];
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      promises.push(dispatch(createVideoFromFile(file, options)));
    }
    return Promise.all(promises);
  };
}

export function createVideo(data) {
  return async (dispatch, getState) => {
    const activeLabelId = selectActiveLabelId(getState());
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NEW_API_HOST}/uploads/videos`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Pragma: 'no-cache',
            'Cache-Control': 'no-cache',
          },
          body: JSON.stringify({
            ...data,
            ...(activeLabelId ? { label_id: activeLabelId } : undefined),
          }),
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      const video = await response.json();
      dispatch(videosActions.upload.createVideo(video));
      return video;
    } catch (error) {
      throw error;
    }
  };
}

export function createVideoWithOptions(options, data = {}) {
  return async (dispatch) => {
    try {
      const video = await dispatch(
        createVideo({
          ...data,
          upload_source: options.upload_source,
          title: options.title,
          status: 1,
        })
      );
      const { shortcode, token } = video;
      options.shortcode = shortcode;
      options.token = token;
      await new Promise((resolve, reject) => {
        transcode(options, (error) => {
          if (error) {
            return reject(error);
          }
          return resolve();
        });
      });
      dispatch(uploadAction(data));
      return video;
    } catch (error) {
      // Unique 8 character shortcode to avoid duplicates or conflicts with existing shortcodes.
      const uniqueShortcode = generateUniqueId('alpha', 8);
      Sentry.captureException(error);
      dispatch(
        createAction(SHORTCODE_RECEIVED)({
          shortcode: options.shortcode || uniqueShortcode,
        })
      );
      dispatch(
        editVideoAction({
          shortcode: options.shortcode || uniqueShortcode,
          status: MEDIA_STATUS.ERROR,
          error: error.message,
        })
      );
      dispatch(
        log(
          `[FE Error] createVideoWithOptions failed for ${
            options.shortcode || uniqueShortcode
          } with error: ${error.message}`
        )
      );
    }
  };
}

export function createVideoFromUrl(url, options = {}, data = {}) {
  return (dispatch, getState) => {
    options.url = url;

    return dispatch(createVideoWithOptions(options, data));
  };
}

export function createVideoFromUrls(urls, options = {}, data = {}) {
  return (dispatch, getState) => {
    options.urls = urls;

    return dispatch(createVideoWithOptions(options, data));
  };
}

export function pollVideo(shortcode, targetVersion) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const video = getVideo(getState().videos.cache, shortcode);
      if (targetVersion && video.version === targetVersion) {
        resolve();
      } else {
        dispatch(fetchVideo(shortcode, targetVersion))
          .then(() => {
            setTimeout(() => {
              const shortcode = getState().video.shortcode;
              if (!shortcode) {
                reject();
                return;
              }
              dispatch(pollVideo(shortcode, targetVersion))
                .then(resolve)
                .catch(reject);
            }, 500);
          })
          .catch(reject);
      }
    });
  };
}

export function screenshot(shortcode, options = {}) {
  return (dispatch) => {
    return fetch(
      `${process.env.REACT_APP_NEW_API_HOST}/screenshots/${shortcode}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(options),
        credentials: 'include',
      }
    ).then(async (response) => {
      const result = await response.json();
      if (!response.ok) {
        dispatch(
          log(
            `[FE Error] Screenshot API call failed for shortcode ${shortcode} with error: ${result.message}`
          )
        );
        throw Error(result.message);
      }
      dispatch(editVideoAction({ ...result, shortcode }));
      return result;
    });
  };
}

export function uploadScreenshot(shortcode, screenshot) {
  return (dispatch) => {
    const formData = new FormData();
    formData.append('screenshot', screenshot);
    return fetch(
      `${process.env.REACT_APP_NEW_API_HOST}/screenshots/${shortcode}/upload`,
      {
        method: 'POST',
        body: formData,
        credentials: 'include',
      }
    ).then(async (response) => {
      const result = await response.json();
      if (!response.ok) {
        dispatch(
          log(
            `[FE Error] uploadScreenshot API call failed for shortcode ${shortcode} with error: ${result.message}`
          )
        );
        throw Error(result.message);
      }
      dispatch(editVideoAction({ ...result, shortcode }));
      return result;
    });
  };
}

export function retranscodeVideo(video, options = {}) {
  return (dispatch) => {
    options.version = (video.max_version || video.version || 0) + 1;
    options.url = `https:${getBestSourceUrl(video)}`;
    options.shortcode = video.shortcode;
    const output = {
      ...video,
      status: 1,
      percent: 0,
      max_version: options.version,
    };
    dispatch(createAction(RETRANSCODE)(output));
    return new Promise((resolve, reject) => {
      transcode(options, (err, data) => {
        if (err) {
          dispatch(createAction(RETRANSCODE)(err));
          reject(err);
          return;
        }
        resolve(output);
      });
    });
  };
}

// Only let the current stats fetch come through, otherwise null it out
function checkStatsResult(state, options, json) {
  const { interval, domain, shortcode } = state;
  /*if (options.interval !== interval
      || options.domain !== domain
      || options.shortcode !== shortcode) {
    return {}
  }*/
  return json;
}

export function fetchRealtimeStats(options = {}) {
  return (dispatch, getState) => {
    const { domain, shortcode } = options;
    if (shortcode) {
      fetch(`${process.env.REACT_APP_API_HOST}/${shortcode}/stats/live`, {
        credentials: 'include',
      })
        .then((response) => response.json())
        .then((json) => {
          dispatch(
            createAction(FETCH_STATS)(
              checkStatsResult(getState().stats, options, { live: json })
            )
          );
        })
        .catch((error) => {
          dispatch(createAction(FETCH_STATS)(error));
        });
    } else if (domain) {
      fetch(`${process.env.REACT_APP_API_HOST}/domain/${domain}/stats/hot`, {
        credentials: 'include',
      })
        .then((response) => response.json())
        .then((json) =>
          dispatch(
            createAction(FETCH_STATS)(
              checkStatsResult(getState().stats, options, { hot: json })
            )
          )
        )
        .catch((error) => dispatch(createAction(FETCH_STATS)({ hot: null })));
      fetch(`${process.env.REACT_APP_API_HOST}/domain/${domain}/stats/live`, {
        credentials: 'include',
      })
        .then((response) => response.json())
        .then((json) =>
          dispatch(
            createAction(FETCH_STATS)(
              checkStatsResult(getState().stats, options, { live: json })
            )
          )
        )
        .catch((error) => dispatch(createAction(FETCH_STATS)({ live: null })));
    } else {
      fetch(`${process.env.REACT_APP_API_HOST}/stats/hot`, {
        credentials: 'include',
      })
        .then((response) => response.json())
        .then((json) =>
          dispatch(
            createAction(FETCH_STATS)(
              checkStatsResult(getState().stats, options, { hot: json })
            )
          )
        )
        .catch((error) => dispatch(createAction(FETCH_STATS)({ hot: null })));
      fetch(`${process.env.REACT_APP_API_HOST}/stats/live`, {
        credentials: 'include',
      })
        .then((response) => response.json())
        .then((json) =>
          dispatch(
            createAction(FETCH_STATS)(
              checkStatsResult(getState().stats, options, { live: json })
            )
          )
        )
        .catch((error) => dispatch(createAction(FETCH_STATS)({ live: null })));
    }
  };
}

export function fetchStats({ interval, domain, shortcode, trending } = {}) {
  return (dispatch, getState) => {
    const options = {
      interval,
      domain,
      shortcode,
      trending,
    };
    dispatch(
      createAction(FETCH_STATS)({
        ...options,
      })
    );

    if (shortcode) {
      fetch(`${process.env.REACT_APP_API_HOST}/${shortcode}/plays`, {
        credentials: 'include',
      })
        .then((response) => response.json())
        .then((json) => {
          dispatch(
            createAction(FETCH_STATS)(
              checkStatsResult(getState().stats, options, { plays: json })
            )
          );
        })
        .catch((error) => {
          dispatch(createAction(FETCH_STATS)(error));
        });
      fetch(`${process.env.REACT_APP_API_HOST}/${shortcode}/articles`, {
        credentials: 'include',
      })
        .then((response) => response.json())
        .then((json) =>
          dispatch(
            createAction(FETCH_STATS)(
              checkStatsResult(getState().stats, options, { articles: json })
            )
          )
        )
        .catch((error) => dispatch(createAction(FETCH_STATS)(error)));
    } else if (domain) {
      fetch(
        `${process.env.REACT_APP_API_HOST}/domain/${domain}/stats?interval=${interval}`,
        { credentials: 'include' }
      )
        .then((response) => response.json())
        .then((json) =>
          dispatch(
            createAction(FETCH_STATS)(
              checkStatsResult(getState().stats, options, json)
            )
          )
        )
        .catch((error) => dispatch(createAction(FETCH_STATS)(error)));
    } else if (trending) {
      fetch(`${process.env.REACT_APP_API_HOST}/trending/${trending}/articles`, {
        credentials: 'include',
      })
        .then((response) => response.json())
        .then((json) =>
          dispatch(
            createAction(FETCH_STATS)(
              checkStatsResult(getState().stats, options, { articles: json })
            )
          )
        )
        .catch((error) => dispatch(createAction(FETCH_STATS)(error)));
    } else {
      const urlRoot = `${process.env.REACT_APP_API_HOST}/stats/`;
      fetch(`${urlRoot}plays`, { credentials: 'include' })
        .then((response) => response.json())
        .then((json) =>
          dispatch(
            createAction(FETCH_STATS)(
              checkStatsResult(getState().stats, options, { plays: json })
            )
          )
        )
        .catch((error) => dispatch(createAction(FETCH_STATS)({ plays: null })));
    }

    dispatch(fetchRealtimeStats({ interval, domain, shortcode }));
  };
}

export function excludeArticle(domain, article, shortcode) {
  return (dispatch, getState) => {
    return fetch(`${process.env.REACT_APP_API_HOST}/domain/${domain}/exclude`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({
        article,
        shortcode,
      }),
    })
      .then((response) => response.json())
      .then((json) => dispatch(createAction(ARTICLE_EXCLUDED)(json)));
  };
}

export function fetchVideos(options, force) {
  return (dispatch, getState) => {
    const { videos } = getState();

    // shallow compare options and video state to see if we're getting new
    let shouldClear = !!force;
    for (const key of Object.keys(options)) {
      if (videos[key] !== options[key]) {
        shouldClear = true;
      }
    }

    if (shouldClear) {
      dispatch(clearVideosAction());
    }

    if (shouldClear || !videos.loaded) {
      dispatch(videosActions.dashboard.fetchVideos.initiated(options));

      return createFetchVideoRequest(options)
        .then(({ total, videos }) => {
          dispatch(
            videosActions.dashboard.fetchVideos.fulfilled({
              total,
              videos: videos.map(transformVideoAfterFetch),
            })
          );
        })
        .catch((error) => {
          Sentry.captureException(error);
          dispatch(videosActions.dashboard.fetchVideos.failed(error));
        });
    }
  };
}

export function login(email, password) {
  return (dispatch) => {
    dispatch(createAction(LOGIN)({ loggingIn: true }));
    return fetch(`${process.env.REACT_APP_API_HOST}/check`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({
        username: email,
        password,
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const json = await response.json();
        if (json.error) {
          throw new Error(json.message);
        }
        dispatch(subscriptionActions.fetchSubscriptionInfo());
        await dispatch(createAction(FETCH_CURRENT_USER)(json));
        await dispatch(fetchExtraUserInfo('flags'));
        await dispatch(
          createAction(LOGIN)({
            loggingIn: false,
            error: false,
            message: null,
            ...json,
          })
        );
        onUserIsFetched(json, { activateDarkMode: true });

        return json;
      })
      .catch((error) => {
        Sentry.captureException(error);
        dispatch(createAction(LOGIN)(error));
      });
  };
}

export function extConnect(authToken, email, provider) {
  return (dispatch) => {
    let error = false;
    dispatch(createAction(LOGIN)({ loggingIn: true }));
    return fetch(`${process.env.REACT_APP_API_HOST}/extconnect`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({
        authToken,
        email,
        provider,
      }),
    })
      .then((response) => response.json())
      .then(async (json) => {
        if (json.error) {
          throw new Error(json.message);
        }
        await dispatch(createAction(LOGIN)({ loggingIn: false, ...json }));
        dispatch(subscriptionActions.fetchSubscriptionInfo());
        await dispatch(fetchExtraUserInfo('flags'));
        onUserIsFetched(json, { activateDarkMode: true });

        return json;
      })
      .catch((err) => {
        Sentry.captureException(err);
        dispatch(createAction(LOGIN)(err));
      });
  };
}

export function signup(username, password, email, verificationRedirect) {
  return (dispatch) => {
    let error = false;
    dispatch(createAction(LOGIN)({ loggingIn: true }));
    return fetch(`${process.env.REACT_APP_API_HOST}/users`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({
        username,
        password,
        email,
        verification_redirect: verificationRedirect,
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const user = await response.json();
        await dispatch(createAction(LOGIN)({ loggingIn: false, ...user }));
        dispatch(subscriptionActions.fetchSubscriptionInfo());
        await dispatch(fetchExtraUserInfo('flags'));
        onUserIsFetched(user);

        return user;
      })
      .catch((err) => {
        Sentry.captureException(err);
        dispatch(createAction(LOGIN)(err));
      });
  };
}

export function resendVerificationEmail() {
  return (dispatch, getState) => {
    return fetch(`${process.env.REACT_APP_NEW_API_HOST}/me/verify`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
    });
  };
}

export function checkTranscodes() {
  return (dispatch, getState) => {
    const uploads = getUploads(getState()).filter((v) => v.status == 1);
    const waiting = getAllVideos(getState()).filter(
      (v) => v.waitingToTranscode
    );

    waiting.forEach((v) => {
      const {
        shortcode,
        waitingToTranscode,
        transcode_options,
        upload_metadata,
      } = v;
      if (uploads.length > 2) {
        dispatch(editVideoAction({ queued: true, shortcode }));
      } else {
        dispatch(editVideoAction({ waitingToTranscode: false, shortcode }));

        const transcoderOptions = {
          ...transcode_options,
          ...upload_metadata.transcoder_options,
        };
        transcode(transcoderOptions, (err, data) => {
          if (err) {
            dispatch(
              log(
                `[FE Error] transcode call from checkTranscodes failed for shortcode ${shortcode} with error: ${err.message}`
              )
            );
            dispatch(errorVideo(shortcode, err.message));
            return;
          }
          dispatch(editVideoAction({ status: 1, shortcode }));
        });
      }
    });
  };
}

export function pollUploads() {
  return (dispatch, getState) => {
    const uploads = getAllVideos(getState()).filter((v) => v.status == 1);
    if (uploads.length) {
      const timeout = setTimeout(() => {
        dispatch(editUserAction({ networkerror: true }));
      }, 5000);

      uploads.forEach(({ shortcode, max_version: maxVersion }) => {
        fetch(
          `${process.env.REACT_APP_NEW_API_HOST}/videos/${shortcode}?version=${
            maxVersion || 0
          }`,
          {
            headers: {
              'Content-Type': 'application/json',
              Pragma: 'no-cache',
              'Cache-Control': 'no-cache',
            },
            credentials: 'include',
          }
        )
          .then(async (response) => {
            if (!response.ok) {
              throw Error(await response.text());
            }
            return response.json();
          })
          .then((video) => {
            dispatch(createAction(POLLING_UPDATE_RECEIVED)([video]));
            dispatch(editUserAction({ networkerror: false }));
            clearTimeout(timeout);
          })
          .catch((error) => {
            dispatch(
              log(
                `[poll2] - Failed to fetch progress of ${shortcode} version ${maxVersion}: ${error}`
              )
            );
            dispatch(editUserAction({ networkerror: true }));
            clearTimeout(timeout);
          });
      });
    }
  };
}

export function editUser(data) {
  return (dispatch) => {
    dispatch(editUserAction(data));

    setDarkModeCookie(data);
    if (isDarkModeEnabled()) {
      enableDarkMode();
    } else {
      disableDarkMode();
    }

    return fetch(`${process.env.REACT_APP_API_HOST}/me`, {
      credentials: 'include',
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({
        session: Cookies.get('session'),
        ...data,
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          throw Error(await response.text());
        }
        return response.json();
      })
      .then((json) => {
        dispatch(editUserAction(json));
      })
      .catch((error) => {
        Sentry.captureException(error);
        alert(error.message);
      });
  };
}

export function uploadPic(photo) {
  return (dispatch, getState) => {
    const formData = new FormData();
    formData.append('watermark', photo);

    return fetch(`${process.env.REACT_APP_API_HOST}/me/pic`, {
      credentials: 'include',
      method: 'POST',
      body: formData,
    })
      .then((response) => response.json())
      .then((json) => dispatch(editUserAction(json)));
  };
}

export function changePassword(data) {
  return (dispatch, getState) => {
    dispatch(editUserAction({ isFetching: true }));
    return fetch(`${process.env.REACT_APP_API_HOST}/me/change_password`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({
        session: Cookies.get('session'),
        ...data,
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          throw Error(await response.text());
        }
        return response.json();
      })
      .then((json) => {
        if (json.error) {
          throw new Error(json.message);
        }
        json.isFetching = false;
        return dispatch(editUserAction(json));
      })
      .catch((error) => {
        Sentry.captureException(error);
        dispatch(editUserAction(error));
      });
  };
}

export function deleteVideo(video) {
  return (dispatch) => {
    if (video.shortcode) {
      dispatch(videosActions.deleteVideo({ isDeleting: true, ...video }));
      return fetch(
        `${process.env.REACT_APP_NEW_API_HOST}/videos/${video.shortcode}`,
        {
          credentials: 'include',
          method: 'DELETE',
        }
      )
        .then(async (response) => await response.text())
        .then(() => {
          dispatch(videosActions.deleteVideo({ ...video, isDeleting: false }));
        })
        .catch((error) => dispatch(videosActions.deleteVideo(error)));
    }
    dispatch(videosActions.deleteVideo({ isDeleting: false, ...video }));
  };
}

export function deleteSelectedVideos() {
  return (dispatch, getState) => {
    const promises = [];

    for (const video of getSelectedVideos(getState())) {
      promises.push(dispatch(deleteVideo(video)));
    }

    Promise.all(promises).then(() => {
      dispatch(deselectAllAction());
    });
  };
}

export function extractFromUrl(sourceUrl, groupId, retries = 0) {
  return (dispatch, getState) => {
    dispatch(createAction(EXTRACT_URL)({ isFetching: true }));

    const searchParams = new URLSearchParams();
    searchParams.append('url', sourceUrl);

    return fetch(
      `${process.env.REACT_APP_NEW_API_HOST}/extract?${searchParams}`,
      {
        credentials: 'include',
      }
    )
      .then((response) => response.json())
      .then((json) => {
        dispatch(
          createAction(EXTRACT_URL)({ isFetching: false, retries, ...json })
        );
      })
      .catch((error) => {
        Sentry.captureException(error);
        dispatch(createAction(EXTRACT_URL)(error));
      });
  };
}

export function loadFile(file, groupId) {
  return (dispatch, getState) => {
    dispatch(
      createAction(EXTRACT_URL)({
        playback_url: window.URL.createObjectURL(file),
        mime: file.type,
        file,
      })
    );
  };
}

export function cancel(video, source) {
  return (dispatch, getState) => {
    if (!video.max_version) {
      if (video.shortcode && video.shortcode !== video.local_id) {
        cancelVideo(video.shortcode);
      } else {
        if (video.shortcode && video.shortcode.length > 6) {
          dispatch(
            log(
              `[cancelVideo] request from ${source} has long shortcode ${video.shortcode}: source=${video.upload_source}, status=${video.status}, error=${video.error}`
            )
          );
        }
      }
      dispatch(videosActions.deleteVideo(video));
    } else {
      dispatch(cancelVideoEdits(video));
    }

    if (video.task) {
      video.task.cancel();
    }
  };
}

export function clearStats() {
  return (dispatch, getState) => {
    dispatch(
      createAction(FETCH_STATS)({
        plays: null,
        hot: null,
        pages: null,
        videos: null,
        live: null,
        vtrs: null,
      })
    );
  };
}
